import { Button } from "@progress/kendo-react-buttons";
import {
  Chart,
  ChartCategoryAxis,
  ChartCategoryAxisItem,
  ChartLegend,
  ChartSeries,
  ChartSeriesItem,
  ChartTooltip,
  ChartValueAxis,
  ChartValueAxisItem,
} from "@progress/kendo-react-charts";
import {
  Calendar,
  DatePicker,
  DateRangePickerChangeEvent,
  SelectionRange,
} from "@progress/kendo-react-dateinputs";
import { DropDownList } from "@progress/kendo-react-dropdowns";
import { ExcelExport } from "@progress/kendo-react-excel-export";
import { Grid, GridColumn } from "@progress/kendo-react-grid";
import { NumericTextBox, RadioGroup } from "@progress/kendo-react-inputs";
import { Error, Label } from "@progress/kendo-react-labels";
import { Tooltip } from "@progress/kendo-react-tooltip";
import { useQuery } from "@tanstack/react-query";
import { useMemo, useRef, useState } from "react";
import analyticsApi from "../../api/analytics";
import productsApi from "../../api/products";
import { CommodityType } from "../../types/commodityType";
import DateUtility from "../../utilities/dateUtilities";
import CanopyDateRangePicker from "../functional/CanopyDateRangePicker";
import { LoadingIndicator } from "../LoadingIndicator";

type SettlementViewMode = "fixedMonth" | "promptOffset" | "promptSpread";

const viewModeOptions = [
  { label: "Fixed Contract Month", value: "fixedMonth" },
  { label: "Prompt + N", value: "promptOffset" },
  { label: "Prompt Spread", value: "promptSpread" },
];

interface SettlementData {
  markDate: string;
  primarySettlementValue: number | null;
  averageSettlementValue: number | null;
  primaryTicker: string;
  averageTicker: string;
}

interface SettlementByPromptOffsetData {
  markDate: string;
  contractMonth: string;
  promptOffset: number;
  primarySettlementValue: number | null;
  primaryTicker: string;
}

interface SettlementSpreadData {
  markDate: string;
  primaryTicker: string;
  contractMonthNear: string;
  settlementValueNear: number | null;
  contractMonthFar: string;
  settlementValueFar: number | null;
  spread: number | null;
}

interface PromptCurveValue {
  offset: number;
  value: number | null;
  contractMonth: string | null;
}

interface DisplayRow {
  markDate: string;
  primarySettlementValue: number | null;
  averageSettlementValue: number | null;
  primaryTicker: string;
  averageTicker: string;
  contractMonth: string | null;
  averageContractMonth: string | null;
  contractMonthFar: string | null;
  curves: PromptCurveValue[] | null;
  // Prompt+N mode also flattens curve values/contracts onto dynamic fields
  // (promptValue0, promptContract0, promptValue1, ...) so the Grid/Excel
  // export - which read a field name off each column - can bind to them.
  [key: string]: unknown;
}

const promptValueField = (offset: number) => `promptValue${offset}`;
const promptContractField = (offset: number) => `promptContract${offset}`;

const getDefaultMarkDateRange = (selectedDate: Date | null, maxMarkDate: Date): SelectionRange => {
  if (!selectedDate) {
    return { start: null, end: null };
  }
  const previousMonth = new Date(selectedDate.getFullYear(), selectedDate.getMonth() - 1, 1);
  const firstDayOfPreviousMonth = new Date(
    previousMonth.getFullYear(),
    previousMonth.getMonth(),
    1,
  );
  const lastDayOfPreviousMonth = new Date(
    previousMonth.getFullYear(),
    previousMonth.getMonth() + 1,
    0,
  );
  const end = lastDayOfPreviousMonth < maxMarkDate ? lastDayOfPreviousMonth : maxMarkDate;
  return { start: firstDayOfPreviousMonth, end };
};

export const Settlement = () => {
  const [selectedCommodity, setSelectedCommodity] = useState<any | null>(null);
  const [selectedDate, setSelectedDate] = useState<Date | null>(() => {
    const today = new Date();
    return new Date(today.getFullYear(), today.getMonth(), 1);
  });
  const [promptOffset, setPromptOffset] = useState<number>(0);
  const [promptOffsetNear, setPromptOffsetNear] = useState<number>(0);
  const [promptOffsetFar, setPromptOffsetFar] = useState<number>(1);
  const [viewMode, setViewMode] = useState<SettlementViewMode>("fixedMonth");
  const [markDateRange, setMarkDateRange] = useState<SelectionRange | null>(null);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const { data: commoditiesData, isPending: commoditiesLoading } = useQuery({
    queryKey: ["commoditiesForSettlement"],
    queryFn: () => productsApi.getCommoditiesForSettlement(),
  });
  const commodities = (commoditiesData?.commodities || [])
    .map((commodity: any) => ({
      ...commodity,
      displayName: `${commodity.abbreviation} (${commodity.name})`,
    }))
    .sort((a: any, b: any) => a.displayName.localeCompare(b.displayName));

  // Calculate date range from 12 months ago to next month
  const today = new Date();
  const todayDateOnly = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  // Real settlement data only exists through yesterday
  const yesterdayDateOnly = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 1);
  const firstOfCurrentMonth = new Date(today.getFullYear(), today.getMonth(), 1);
  const twelveMonthsAgo = new Date();
  twelveMonthsAgo.setMonth(today.getMonth() - 10); // 12 months total: 11 months ago through next month
  const nextMonth = new Date(today.getFullYear(), today.getMonth() + 1, 1);
  const twelveMonthsAgoMonth = new Date(
    twelveMonthsAgo.getFullYear(),
    twelveMonthsAgo.getMonth(),
    1,
  );

  const fixedModeDefaultRange = useMemo(
    () => getDefaultMarkDateRange(selectedDate, todayDateOnly),
    [selectedDate, todayDateOnly],
  );
  const promptOffsetDefaultRange = useMemo(
    () => getDefaultMarkDateRange(firstOfCurrentMonth, yesterdayDateOnly),
    [firstOfCurrentMonth, yesterdayDateOnly],
  );
  const defaultMarkDateRange =
    viewMode === "fixedMonth" ? fixedModeDefaultRange : promptOffsetDefaultRange;
  const effectiveMarkDateRange = markDateRange ?? defaultMarkDateRange;

  // Mark dates can't run later than the last day of the previous month, or later than today
  const maxMarkDate = defaultMarkDateRange.end;

  const ticker = selectedCommodity?.abbreviation || "";

  // Format the observation date as YYYY-MM-01 (first day of the selected month)
  const observationDate = selectedDate
    ? `${selectedDate.getFullYear()}-${String(selectedDate.getMonth() + 1).padStart(2, "0")}-01`
    : "";

  const startMarkDate = effectiveMarkDateRange.start
    ? DateUtility.formatDateYMD(effectiveMarkDateRange.start)
    : "";
  const endMarkDate = effectiveMarkDateRange.end
    ? DateUtility.formatDateYMD(effectiveMarkDateRange.end)
    : "";

  const {
    data: fixedMonthData,
    isFetching: isFixedMonthFetching,
    isPending: isFixedMonthPending,
    isError: isFixedMonthError,
    error: fixedMonthError,
    refetch: refetchFixedMonth,
  } = useQuery({
    queryKey: ["settlement", ticker, observationDate, startMarkDate, endMarkDate],
    queryFn: () => analyticsApi.getSettlement(ticker, observationDate, startMarkDate, endMarkDate),
    enabled: false, // Disable automatic fetching
  });

  const {
    data: promptOffsetData,
    isFetching: isPromptOffsetFetching,
    isPending: isPromptOffsetPending,
    isError: isPromptOffsetError,
    error: promptOffsetError,
    refetch: refetchPromptOffset,
  } = useQuery({
    queryKey: ["settlementByPromptOffset", ticker, promptOffset, startMarkDate, endMarkDate],
    queryFn: () =>
      analyticsApi.getSettlementByPromptOffset(ticker, promptOffset, startMarkDate, endMarkDate),
    enabled: false, // Disable automatic fetching
  });

  const {
    data: spreadData,
    isFetching: isSpreadFetching,
    isPending: isSpreadPending,
    isError: isSpreadError,
    error: spreadError,
    refetch: refetchSpread,
  } = useQuery({
    queryKey: [
      "settlementSpread",
      ticker,
      promptOffsetNear,
      promptOffsetFar,
      startMarkDate,
      endMarkDate,
    ],
    queryFn: () =>
      analyticsApi.getSettlementSpread(
        ticker,
        promptOffsetNear,
        promptOffsetFar,
        startMarkDate,
        endMarkDate,
      ),
    enabled: false, // Disable automatic fetching
  });

  const isFetching =
    viewMode === "fixedMonth"
      ? isFixedMonthFetching
      : viewMode === "promptOffset"
        ? isPromptOffsetFetching
        : isSpreadFetching;
  const isPending =
    viewMode === "fixedMonth"
      ? isFixedMonthPending
      : viewMode === "promptOffset"
        ? isPromptOffsetPending
        : isSpreadPending;
  const isError =
    viewMode === "fixedMonth"
      ? isFixedMonthError
      : viewMode === "promptOffset"
        ? isPromptOffsetError
        : isSpreadError;
  const error =
    viewMode === "fixedMonth"
      ? fixedMonthError
      : viewMode === "promptOffset"
        ? promptOffsetError
        : spreadError;
  const hasData =
    viewMode === "fixedMonth"
      ? !!fixedMonthData
      : viewMode === "promptOffset"
        ? !!promptOffsetData
        : !!spreadData;

  const _export = useRef<ExcelExport>(null);
  const _grid = useRef<any>(null);

  const settlementData: DisplayRow[] = useMemo(() => {
    if (viewMode === "fixedMonth") {
      const averageContractMonth = selectedDate
        ? new Date(selectedDate.getFullYear(), selectedDate.getMonth() - 1, 1)
            .toISOString()
            .slice(0, 7)
        : null;

      return (fixedMonthData?.data || []).map((item: SettlementData) => ({
        markDate: DateUtility.formatDateYMD(item.markDate),
        primarySettlementValue: item.primarySettlementValue,
        averageSettlementValue: item.averageSettlementValue,
        primaryTicker: item.primaryTicker,
        averageTicker: item.averageTicker,
        contractMonth: observationDate ? observationDate.slice(0, 7) : null,
        averageContractMonth: item.averageTicker ? averageContractMonth : null,
        contractMonthFar: null,
        curves: null,
      }));
    }

    if (viewMode === "promptOffset") {
      const rowsByMarkDate = new Map<string, DisplayRow>();

      for (const item of promptOffsetData?.data || ([] as SettlementByPromptOffsetData[])) {
        const markDate = DateUtility.formatDateYMD(item.markDate);
        const contractMonth = DateUtility.formatDateYMD(item.contractMonth).slice(0, 7);

        let row = rowsByMarkDate.get(markDate);
        if (!row) {
          row = {
            markDate,
            primarySettlementValue: null,
            averageSettlementValue: null,
            primaryTicker: item.primaryTicker,
            averageTicker: "",
            contractMonth: null,
            averageContractMonth: null,
            contractMonthFar: null,
            curves: [],
          };
          rowsByMarkDate.set(markDate, row);
        }

        row.curves!.push({ offset: item.promptOffset, value: item.primarySettlementValue, contractMonth });
        row[promptValueField(item.promptOffset)] = item.primarySettlementValue;
        row[promptContractField(item.promptOffset)] = contractMonth;
      }

      return Array.from(rowsByMarkDate.values())
        .map((row) => ({ ...row, curves: row.curves!.sort((a, b) => a.offset - b.offset) }))
        .sort((a, b) => a.markDate.localeCompare(b.markDate));
    }

    return (spreadData?.data || []).map((item: SettlementSpreadData) => ({
      markDate: DateUtility.formatDateYMD(item.markDate),
      primarySettlementValue: item.spread,
      averageSettlementValue: null,
      primaryTicker: item.primaryTicker,
      averageTicker: "",
      contractMonth: DateUtility.formatDateYMD(item.contractMonthNear).slice(0, 7),
      averageContractMonth: null,
      contractMonthFar: DateUtility.formatDateYMD(item.contractMonthFar).slice(0, 7),
      curves: null,
      spreadNearValue: item.settlementValueNear,
      spreadFarValue: item.settlementValueFar,
    }));
  }, [viewMode, fixedMonthData, promptOffsetData, spreadData, observationDate, selectedDate]);

  // The set of curve offsets (0..N) to render for the Prompt+N view, derived from the
  // submitted promptOffset so every column/series still appears even if a mark date is
  // missing data for a given rank.
  const promptCurveOffsets = useMemo(
    () => Array.from({ length: promptOffset + 1 }, (_, i) => i),
    [promptOffset],
  );

  // Determine decimal places based on commodity type (5 for Propane, 4 for others)
  const decimalPlaces = useMemo(() => {
    return selectedCommodity?.commodityTypeId === CommodityType.Propane ? 5 : 4;
  }, [selectedCommodity]);

  const handleSelectedDateChange = (value: Date | null) => {
    setSelectedDate(value);
    // A custom range's bounds no longer make sense once the contract month changes
    setMarkDateRange(null);
  };

  const handleViewModeChange = (value: SettlementViewMode) => {
    setViewMode(value);
    // The default mark date range differs between modes
    setMarkDateRange(null);
  };

  const isRunReportDisabled =
    !selectedCommodity ||
    !effectiveMarkDateRange.start ||
    !effectiveMarkDateRange.end ||
    (viewMode === "fixedMonth" && !selectedDate) ||
    (viewMode === "promptSpread" && promptOffsetFar <= promptOffsetNear);

  const handleRunReport = () => {
    if (isRunReportDisabled) {
      return;
    }
    setIsSubmitted(true);
    if (viewMode === "fixedMonth") {
      refetchFixedMonth();
    } else if (viewMode === "promptOffset") {
      refetchPromptOffset();
    } else {
      refetchSpread();
    }
  };

  const handleEdit = () => {
    setIsSubmitted(false);
  };

  const excelExport = () => {
    if (_export.current && _grid.current) {
      const roundedData = settlementData.map((item: DisplayRow) => {
        const rounded: DisplayRow = {
          ...item,
          primarySettlementValue:
            item.primarySettlementValue != null
              ? parseFloat(item.primarySettlementValue.toFixed(decimalPlaces))
              : item.primarySettlementValue,
          averageSettlementValue:
            item.averageSettlementValue != null
              ? parseFloat(item.averageSettlementValue.toFixed(decimalPlaces))
              : item.averageSettlementValue,
          spreadNearValue:
            item.spreadNearValue != null
              ? parseFloat((item.spreadNearValue as number).toFixed(decimalPlaces))
              : item.spreadNearValue,
          spreadFarValue:
            item.spreadFarValue != null
              ? parseFloat((item.spreadFarValue as number).toFixed(decimalPlaces))
              : item.spreadFarValue,
        };
        for (const offset of promptCurveOffsets) {
          const value = item[promptValueField(offset)] as number | null | undefined;
          rounded[promptValueField(offset)] = value != null ? parseFloat(value.toFixed(decimalPlaces)) : value;
        }
        return rounded;
      });
      _export.current.save(roundedData, _grid.current.columns);
    }
  };

  // Prepare chart data - show all data points with readable labels
  const chartData = useMemo(() => {
    return settlementData.map((item: DisplayRow) => {
      // Parse the date in local timezone to avoid day shift issues
      const [year, month, day] = item.markDate.split("-").map(Number);
      const localDate = new Date(year, month - 1, day);

      const point: Record<string, unknown> = {
        category: localDate.toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
          year: "2-digit",
        }),
        primaryValue: item.primarySettlementValue,
        averageValue: item.averageSettlementValue,
        exactDate: item.markDate, // Store the exact yyyy-MM-dd format for tooltip
        contractMonth: item.contractMonth,
        averageContractMonth: item.averageContractMonth,
        contractMonthFar: item.contractMonthFar,
      };

      if (item.curves) {
        for (const curve of item.curves) {
          point[promptValueField(curve.offset)] = curve.value;
          point[promptContractField(curve.offset)] = curve.contractMonth;
        }
      }

      return point;
    });
  }, [settlementData]);

  return (
    <div style={{ padding: "1rem" }}>
      <div
        style={{
          marginBottom: "1rem",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: "1rem",
          flexWrap: "wrap",
        }}
      >
        {isSubmitted ? (
          <>
            <div style={{ display: "flex", flexDirection: "column", gap: "0.25rem" }}>
              <Label>View:</Label>
              <div>
                {viewMode === "fixedMonth"
                  ? "Fixed Contract Month"
                  : viewMode === "promptOffset"
                    ? "Prompt + N"
                    : "Prompt Spread (Near-Far)"}
              </div>
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: "0.25rem" }}>
              <Label>Commodity:</Label>
              <div>{selectedCommodity?.displayName}</div>
            </div>

            {viewMode === "fixedMonth" ? (
              <div style={{ display: "flex", flexDirection: "column", gap: "0.25rem" }}>
                <Label>Contract Month:</Label>
                <div>{DateUtility.formatDateMY(selectedDate!)}</div>
              </div>
            ) : viewMode === "promptOffset" ? (
              <div style={{ display: "flex", flexDirection: "column", gap: "0.25rem" }}>
                <Label>Prompt + N:</Label>
                <div>{promptOffset}</div>
              </div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: "0.25rem" }}>
                <Label>Prompt + Near / Far:</Label>
                <div>
                  {promptOffsetNear} / {promptOffsetFar}
                </div>
              </div>
            )}

            <div style={{ display: "flex", flexDirection: "column", gap: "0.25rem" }}>
              <Label>Mark Dates:</Label>
              <div>
                {startMarkDate} to {endMarkDate}
              </div>
            </div>

            <div style={{ alignSelf: "flex-end" }}>
              {isFetching ? (
                <LoadingIndicator loading={true} />
              ) : (
                <Button themeColor="primary" onClick={handleEdit}>
                  New Report
                </Button>
              )}
            </div>
          </>
        ) : (
          <>
            <div style={{ display: "flex", flexDirection: "column", gap: "0.25rem" }}>
              <Label>View:</Label>
              <RadioGroup
                data={viewModeOptions}
                value={viewMode}
                layout="horizontal"
                onChange={(e) => handleViewModeChange(e.value)}
              />
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: "0.25rem" }}>
              <Label>Commodity:</Label>
              <DropDownList
                data={commodities}
                value={selectedCommodity}
                textField="displayName"
                dataItemKey="id"
                style={{ width: 400 }}
                onChange={(e) => setSelectedCommodity(e.target.value)}
                disabled={commoditiesLoading}
              />
              {commoditiesLoading && <LoadingIndicator loading={true} />}
            </div>

            {viewMode === "fixedMonth" ? (
              <div style={{ display: "flex", flexDirection: "column", gap: "0.25rem" }}>
                <Label>Contract Month:</Label>
                <DatePicker
                  value={selectedDate}
                  onChange={(e) => handleSelectedDateChange(e.target.value)}
                  calendar={(props: any) => (
                    <Calendar bottomView="year" topView="year" {...props} />
                  )}
                  format="MMM yyyy"
                  style={{ width: 400 }}
                  min={twelveMonthsAgoMonth}
                  max={nextMonth}
                />
              </div>
            ) : viewMode === "promptOffset" ? (
              <div style={{ display: "flex", flexDirection: "column", gap: "0.25rem" }}>
                <Label>Prompt + N:</Label>
                <NumericTextBox
                  value={promptOffset}
                  onChange={(e) => setPromptOffset(e.value ?? 0)}
                  min={0}
                  max={35}
                  format="n0"
                  width={100}
                />
              </div>
            ) : (
              <>
                <div style={{ display: "flex", flexDirection: "column", gap: "0.25rem" }}>
                  <Label>Prompt + Near:</Label>
                  <NumericTextBox
                    value={promptOffsetNear}
                    onChange={(e) => setPromptOffsetNear(e.value ?? 0)}
                    min={0}
                    max={35}
                    format="n0"
                    width={100}
                  />
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: "0.25rem" }}>
                  <Label>Prompt + Far:</Label>
                  <NumericTextBox
                    value={promptOffsetFar}
                    onChange={(e) => setPromptOffsetFar(e.value ?? 0)}
                    min={0}
                    max={35}
                    format="n0"
                    width={100}
                  />
                </div>
              </>
            )}

            <div style={{ display: "flex", flexDirection: "column", gap: "0.25rem" }}>
              <Label>Mark Dates:</Label>
              <CanopyDateRangePicker
                value={effectiveMarkDateRange}
                onChange={(e: DateRangePickerChangeEvent) => setMarkDateRange(e.value)}
                max={maxMarkDate ?? undefined}
                startDateInputSettings={{ label: "", ariaLabel: "Start" }}
                endDateInputSettings={{ label: "", ariaLabel: "End" }}
              />
            </div>

            <div style={{ alignSelf: "flex-end" }}>
              <Button themeColor="primary" onClick={handleRunReport} disabled={isRunReportDisabled}>
                Run Report
              </Button>
            </div>
          </>
        )}

        {isSubmitted && settlementData.length > 0 && (
          <Button themeColor="primary" onClick={excelExport} style={{ marginLeft: "auto" }}>
            Export to Excel
          </Button>
        )}
      </div>

      {isSubmitted && isError && (
        <Error>
          Error loading settlement data{error instanceof Error ? ": " + error.message : ""}
        </Error>
      )}

      {isSubmitted && settlementData.length > 0 && (
        <Tooltip anchorElement="target" position="top" parentTitle={true}>
          <ExcelExport
            ref={_export}
            fileName={
              viewMode === "promptOffset"
                ? `Settlement_${ticker}_Prompt${promptOffset}.xlsx`
                : viewMode === "promptSpread"
                  ? `Settlement_${ticker}_Spread_Near${promptOffsetNear}_Far${promptOffsetFar}.xlsx`
                  : `Settlement_${ticker}_${observationDate}.xlsx`
            }
          />
          <div style={{ display: "flex", gap: "1rem" }}>
            <div style={{ flex: 1 }}>
              <Chart style={{ height: 400 }}>
                <ChartLegend visible={true} position="top" />
                <ChartTooltip
                  render={(e: any) => {
                    const dataItem = e.point?.dataItem;
                    if (!dataItem) return "";

                    const primaryTicker =
                      settlementData.length > 0 ? settlementData[0]?.primaryTicker || "" : "";
                    const averageTicker =
                      settlementData.length > 0 ? settlementData[0]?.averageTicker || "" : "";
                    const showContractMonth = viewMode === "promptOffset";

                    // Check which series is being hovered
                    const seriesIndex = e.point?.series?.index;
                    const seriesField = e.point?.series?.field as string | undefined;

                    if (viewMode === "promptSpread") {
                      const spreadValue = dataItem.primaryValue
                        ? dataItem.primaryValue.toFixed(decimalPlaces)
                        : "N/A";
                      const contractSuffix =
                        dataItem.contractMonth && dataItem.contractMonthFar
                          ? ` (Near: ${dataItem.contractMonth}, Far: ${dataItem.contractMonthFar})`
                          : "";
                      return `${dataItem.exactDate}: ${primaryTicker} Spread${contractSuffix} ${spreadValue}`;
                    }

                    if (viewMode === "promptOffset" && seriesField?.startsWith("promptValue")) {
                      const offset = Number(seriesField.slice("promptValue".length));
                      const value = dataItem[seriesField];
                      const contractMonth = dataItem[promptContractField(offset)];
                      const formattedValue = value != null ? value.toFixed(decimalPlaces) : "N/A";
                      const contractSuffix = contractMonth ? ` (${contractMonth})` : "";
                      return `${dataItem.exactDate}: ${primaryTicker} Prompt+${offset}${contractSuffix} ${formattedValue}`;
                    }

                    if (seriesIndex === 0) {
                      // Primary series (first line)
                      const primaryValue = dataItem.primaryValue
                        ? dataItem.primaryValue.toFixed(decimalPlaces)
                        : "N/A";
                      const contractSuffix =
                        showContractMonth && dataItem.contractMonth
                          ? ` (${dataItem.contractMonth})`
                          : "";
                      return `${dataItem.exactDate}: ${primaryTicker}${contractSuffix} ${primaryValue}`;
                    } else if (seriesIndex === 1) {
                      // Average series (second line)
                      const averageValue = dataItem.averageValue
                        ? dataItem.averageValue.toFixed(decimalPlaces)
                        : "N/A";
                      const contractSuffix =
                        showContractMonth && dataItem.averageContractMonth
                          ? ` (${dataItem.averageContractMonth})`
                          : "";
                      return `${dataItem.exactDate}: ${averageTicker}${contractSuffix} ${averageValue}`;
                    }

                    // Fallback to showing both if series detection fails
                    const primaryValue = dataItem.primaryValue
                      ? dataItem.primaryValue.toFixed(decimalPlaces)
                      : "N/A";
                    const averageValue = dataItem.averageValue
                      ? dataItem.averageValue.toFixed(decimalPlaces)
                      : "N/A";
                    const averagePart = averageTicker ? `, ${averageTicker} ${averageValue}` : "";
                    return `${dataItem.exactDate}: ${primaryTicker} ${primaryValue}${averagePart}`;
                  }}
                />
                <ChartCategoryAxis>
                  <ChartCategoryAxisItem
                    categories={chartData.map((item) => item.category)}
                    labels={{
                      rotation: -45,
                      step: Math.max(1, Math.ceil(chartData.length / 20)),
                    }}
                  />
                </ChartCategoryAxis>
                <ChartValueAxis>
                  <ChartValueAxisItem labels={{ format: "{0:n2}" }} />
                </ChartValueAxis>
                <ChartSeries>
                  {viewMode === "promptOffset" ? (
                    promptCurveOffsets.map((offset) => (
                      <ChartSeriesItem
                        key={offset}
                        type="line"
                        data={chartData}
                        field={promptValueField(offset)}
                        name={`${settlementData[0]?.primaryTicker || ticker} (Prompt+${offset})`}
                      />
                    ))
                  ) : (
                    [
                      <ChartSeriesItem
                        key="primary"
                        type="line"
                        data={chartData}
                        field="primaryValue"
                        name={
                          viewMode === "promptSpread"
                            ? `${settlementData[0]?.primaryTicker || ticker} Spread (Prompt+${promptOffsetNear} - Prompt+${promptOffsetFar})`
                            : settlementData[0]?.primaryTicker
                              ? `${settlementData[0].primaryTicker} (${observationDate.slice(0, 7)})`
                              : "Primary Settlement"
                        }
                      />,
                      settlementData.length > 0 && settlementData[0]?.averageTicker && (
                        <ChartSeriesItem
                          key="average"
                          type="line"
                          data={chartData}
                          field="averageValue"
                          color="black"
                          name={`${settlementData[0].averageTicker} (${new Date(selectedDate!.getFullYear(), selectedDate!.getMonth() - 1, 1).toISOString().slice(0, 7)})`}
                        />
                      ),
                    ]
                  )}
                </ChartSeries>
              </Chart>
            </div>
            <div style={{ width: viewMode === "promptSpread" ? 400 : viewMode === "promptOffset" ? Math.min(700, 200 + promptCurveOffsets.length * 90) : 340 }}>
              <Grid
                ref={_grid}
                data={settlementData}
                sortable={true}
                autoProcessData={true}
                style={{
                  maxHeight: "400px",
                }}
              >
                <GridColumn field="markDate" title="Mark Date" width="100" />
                {viewMode === "promptSpread" && (
                  <GridColumn
                    field="spreadNearValue"
                    title={`${promptOffsetNear}`}
                    width="90"
                    format={`{0:n${decimalPlaces}}`}
                  />
                )}
                {viewMode === "promptSpread" && (
                  <GridColumn
                    field="spreadFarValue"
                    title={`${promptOffsetFar}`}
                    width="90"
                    format={`{0:n${decimalPlaces}}`}
                  />
                )}
                {viewMode === "promptOffset" &&
                  promptCurveOffsets.map((offset) => (
                    <GridColumn
                      key={offset}
                      field={promptValueField(offset)}
                      title={`${offset}`}
                      width="90"
                      format={`{0:n${decimalPlaces}}`}
                    />
                  ))}
                {viewMode !== "promptOffset" && (
                  <GridColumn
                    field="primarySettlementValue"
                    title={
                      viewMode === "promptSpread"
                        ? "Spread"
                        : settlementData[0]?.primaryTicker
                          ? `${settlementData[0].primaryTicker} (${observationDate.slice(0, 7)})`
                          : "Primary Settlement"
                    }
                    width="90"
                    format={`{0:n${decimalPlaces}}`}
                  />
                )}
                {viewMode !== "promptOffset" && settlementData.length > 0 && settlementData[0]?.averageTicker && (
                  <GridColumn
                    field="averageSettlementValue"
                    title={`${settlementData[0].averageTicker} (${new Date(selectedDate!.getFullYear(), selectedDate!.getMonth() - 1, 1).toISOString().slice(0, 7)})`}
                    width="90"
                    format={`{0:n${decimalPlaces}}`}
                  />
                )}
              </Grid>
            </div>
          </div>
        </Tooltip>
      )}

      {isSubmitted && !isPending && settlementData.length === 0 && hasData && (
        <div style={{ textAlign: "center", padding: "2rem", color: "#666" }}>
          No settlement data found for the selected commodity and date.
        </div>
      )}
    </div>
  );
};
